import React from "react";
import { Row, Col, Form, Button, Card } from "react-bootstrap";
import ConditionRow from "../common/ConditionRow";
import { standardInputStyles, standardLabelStyles } from "../../utils/conditionInputStyles";
import {
  OLD_VALUE_OPERATORS,
  OLD_VALUE_PICKLIST_OPERATORS,
} from "../setup/sharingrule/SharingRuleCriteriaConfig";

function criterionUsesOldValueOperator(criteria) {
  const op = criteria?.operator;
  if (op == null || op === "") return false;
  return String(op).toLowerCase().replace(/\s+/g, "_").startsWith("old_");
}

export default function WorkflowCriteriaConfig({
  criteriaList,
  onAdd,
  onDelete,
  onChange,
  onPatch,
  objectFields,
  /** Includes parent lookup fields (e.g. invoice.amount) for field-to-field criteria values. */
  valueFields = null,
  readOnly,
  disabled = false,
  eventType = "onCreate",
  /** before_save | after_save — before_save enables Old * operators (with onUpdate). */
  triggerEvent = "after_save",
}) {
  const eventTypeArray = Array.isArray(eventType)
    ? eventType
    : typeof eventType === "string" && eventType.includes(",")
      ? eventType.split(",").map((e) => e.trim())
      : [eventType].filter((e) => e);
  const hasOnCreate = eventTypeArray.includes("onCreate");
  const hasOnUpdate = eventTypeArray.includes("onUpdate");
  const isBeforeSave = triggerEvent === "before_save";

  const includeIsChangedOperator = hasOnUpdate;
  const includeOldValueOperators = isBeforeSave && hasOnUpdate;

  const operators = [
    { value: "=", label: "=" },
    { value: "!=", label: "!=" },
    { value: "is_null", label: "Is Null" },
    { value: "is_not_null", label: "Is Not Null" },
    { value: "<", label: "<" },
    { value: ">", label: ">" },
    { value: "<=", label: "<=" },
    { value: ">=", label: ">=" },
    { value: "IN", label: "IN" },
    { value: "NOT IN", label: "NOT IN" },
    { value: "contains", label: "Contains" },
    { value: "starts_with", label: "Starts With" },
    { value: "ends_with", label: "Ends With" },
    ...(includeIsChangedOperator ? [{ value: "isChanged", label: "Is Changed" }] : []),
    ...(includeOldValueOperators ? OLD_VALUE_OPERATORS : []),
  ];

  const picklistOperators = [
    { value: "=", label: "=" },
    { value: "!=", label: "!=" },
    { value: "is_null", label: "Is Null" },
    { value: "is_not_null", label: "Is Not Null" },
    { value: "IN", label: "IN" },
    { value: "NOT IN", label: "NOT IN" },
    ...(includeIsChangedOperator ? [{ value: "isChanged", label: "Is Changed" }] : []),
    ...(includeOldValueOperators ? OLD_VALUE_PICKLIST_OPERATORS : []),
  ];

  const evaluateOptions =
    hasOnCreate && !hasOnUpdate ? ["Always"] : ["Always", "OnChange"];

  const showOldValueWarning =
    isBeforeSave &&
    !hasOnUpdate &&
    criteriaList?.some((c) => criterionUsesOldValueOperator(c));

  return (
    <div className="mt-3">
      {isBeforeSave && hasOnUpdate && (
        <Form.Text className="text-muted small d-block mb-2">
          Before Save with On Update: use <strong>Is Changed</strong> or{" "}
          <strong>Old =</strong> / <strong>Old IN</strong> to compare the previous value (same as
          validation rules).
        </Form.Text>
      )}
      {showOldValueWarning && (
        <Form.Text className="text-warning small d-block mb-2">
          Old-value criteria require <strong>On Update</strong> in Event.
        </Form.Text>
      )}

      {criteriaList != null &&
        criteriaList.length > 0 &&
        criteriaList.map((criteria, index) => {
          const usesOldOp = criterionUsesOldValueOperator(criteria);

          return (
            <Card key={index} className="mb-3 border">
              <Card.Body className="p-4">
                <Row className="align-items-end">
                  <Col md={usesOldOp ? 10 : 8}>
                    <ConditionRow
                      condition={criteria}
                      fields={objectFields}
                      onChange={onChange}
                      index={index}
                      readOnly={readOnly}
                      options={{
                        fieldKey: "fieldName",
                        nameKey: "field_name",
                        labelKey: "field_label",
                        customOperators: operators,
                        picklistOperators,
                        fieldColSize: 4,
                        operatorColSize: 3,
                        valueColSize: 5,
                        valueFields: valueFields || objectFields,
                        allowFieldValue: true,
                        allowLoggedUserValue: true,
                        onPatch,
                      }}
                    />
                  </Col>

                  {!usesOldOp && (
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold" style={standardLabelStyles}>
                          Evaluate On
                        </Form.Label>
                        <Form.Select
                          value={
                            eventTypeArray.length === 1 && hasOnCreate
                              ? "Always"
                              : criteria.operator === "isChanged"
                                ? "OnChange"
                                : criteria.evaluateOn
                          }
                          onChange={(e) => onChange(index, "evaluateOn", e.target.value)}
                          disabled={
                            readOnly ||
                            (eventTypeArray.length === 1 && hasOnCreate) ||
                            criteria.operator === "isChanged"
                          }
                          style={standardInputStyles}
                        >
                          <option value="">-Select-</option>
                          {evaluateOptions.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  )}

                  {!readOnly && (
                    <Col
                      md={usesOldOp ? 2 : 2}
                      className="d-flex align-items-end pb-3"
                    >
                      <Button
                        variant="outline-danger"
                        onClick={() => onDelete(index)}
                        size="sm"
                        disabled={readOnly}
                        className="w-100"
                      >
                        Delete
                      </Button>
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>
          );
        })}

      {criteriaList == null || criteriaList.length === 0 ? (
        <div className="text-center py-4 text-muted">
          <p className="mb-0">No criteria configured. Click &quot;Add Criteria&quot; to get started.</p>
        </div>
      ) : null}

      {!readOnly && (
        <Button
          variant="outline-primary"
          onClick={onAdd}
          disabled={readOnly || disabled}
          title={disabled ? "Please select an object first" : ""}
          className="mt-2"
        >
          <i className="fa fa-plus me-2"></i>
          Add Criteria
        </Button>
      )}
    </div>
  );
}